import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { Info } from 'lucide-react'

/**
 * The detail behind a short sentence, opened by a tap (D-720).
 *
 * `asked`: *"these both seem very wordy and hard to understand here, probably
 * should be a bit more concise with maybe a tooltip to hover over for more
 * details"*. **A tap, not only a hover**: most teachers are on a phone, where
 * hover does not exist, so a hover-only tooltip would hide the detail from
 * exactly the people reading it. A button works for mouse, finger and keyboard.
 *
 * **Opens underneath, in the flow, not floating.** A floating box anchored to
 * the icon ran 82px off a phone whenever the title was long; in the flow it
 * cannot, and the wrapper must be a wrapping flex row for it to sit below.
 *
 * Closes the way the message row's "..." does (D-715): Escape while focus is in
 * it or nowhere, a press anywhere outside, or focus leaving it.
 */
export default function InfoTip({ label, children }: { label: string; children: ReactNode }) {
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLSpanElement>(null)
  const button = useRef<HTMLButtonElement>(null)
  const bodyId = useId()

  useEffect(() => {
    if (!open) return
    const onPointer = (e: PointerEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      const at = document.activeElement
      if (e.key !== 'Escape') return
      if (at && at !== document.body && !box.current?.contains(at)) return
      setOpen(false)
      button.current?.focus()
    }
    document.addEventListener('pointerdown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <span
      ref={box}
      className="contents"
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOpen(false)
      }}
    >
      <button
        ref={button}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls={bodyId}
        aria-label={label}
        className="flex h-6 w-6 items-center justify-center rounded-full text-ink-3 hover:bg-surface-2 hover:text-ink-2"
      >
        <Info size={14} aria-hidden="true" />
      </button>

      {open && (
        <span
          id={bodyId}
          role="note"
          className="mt-1 block basis-full rounded-lg border border-line-2 bg-surface-2 px-3 py-2 text-[12.5px] leading-relaxed font-normal tracking-normal text-ink-2 normal-case"
        >
          {children}
        </span>
      )}
    </span>
  )
}
